import * as React from "react";
import IOPair from "./IOPair";
import icon from "./icon.svg";
import styled from "./styled-components";
import { Word, Operator } from "./InputBox";
import { AndroidView } from "react-device-detect";

export const { Provider, Consumer } = React.createContext({});

interface IState {
  pairs: string[];
  showAbout: boolean;
}

const Title = styled.h1`
  display: flex;
  align-items: center;
  margin: 0;
  padding-top: 1em;
  span {
    margin-left: 0.5em;
  }
`;

const AndroidWarning = styled.div`
  background-color: #c0ffc3;
  border: 1px solid #7de681;
  border-radius: 10px;
  color: #515151;
  margin: 1em;
  padding: 0em 1em 0em 1em;
  h2 {
    margin-bottom: 0.5em;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap-reverse;
`;

const About = styled.div`
  background-color: #ebecef;
  margin: 1em;
  padding: 0em 1em 1em 1em;
  border-radius: 10px;
  border: 1px solid #c4c9d9;
  color: #515151;
  h2 {
    margin-bottom: 0.5em;
  }
  a {
    text-decoration: underline;
    color: #727272;
  }
  p {
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }
  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
    li {
      display: inline-block;
      background-color: #ffffff;
      border: 1px solid #979797;
      border-radius: 5px;
      width: fit-content;
      padding: 0.6em;
      margin: 0.5em;
      font-size: 0.9em;
    }
  }
`;

const FlexChildren = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  span {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const AboutContainer = styled.div<any>`
  overflow: hidden;
  height: ${({ showAbout }) => (showAbout ? "auto" : "0px")};
`;

const CalcContainer = styled.div`
  flex-basis: 500px;
  flex-grow: 1;
  max-height: 100%;
  overflow-y: auto;
`;

export class App extends React.Component<any, IState> {
  public readonly state = {
    pairs: [""],
    showAbout: true
  };
  public onResult = async (id: number, topResult: string) => {
    const newPairs = [...this.state.pairs];
    newPairs[id] = topResult;
    if (id === this.state.pairs.length - 1) {
      newPairs.push("");
    }
    this.setState({ pairs: newPairs });
  };
  public toggleShowAbout = async () => {
    await this.setState({ showAbout: !this.state.showAbout });
    localStorage.setItem(
      "showAbout",
      this.state.showAbout ? "show" : "dismissed"
    );
  };
  public componentDidMount() {
    const sa = localStorage.getItem("showAbout");
    if (sa !== null) {
      this.setState({ showAbout: sa !== "dismissed" });
    }
  }
  public render() {
    const { pairs, showAbout } = this.state;
    return (
      <div className="App">
        <Title>
          <span>
            <img src={icon} width={50} height={50} />
          </span>{" "}
          <span>Semantic Calculator</span>
        </Title>{" "}
        <Content>
          <CalcContainer>
            {pairs.map((_, id) => (
              <IOPair onResult={this.onResult} key={id} id={id} />
            ))}
          </CalcContainer>{" "}
          <AndroidView>
            <AndroidWarning>
              <h2>Sorry!</h2>
              <p>
                This doesn't run well for Android users who also use GBoard. The
                best experience is on desktop.
              </p>
            </AndroidWarning>
          </AndroidView>
          <About>
            <FlexChildren>
              <h2>What's this?</h2>
              <a href="#">
                <span onClick={this.toggleShowAbout}>
                  {showAbout ? "hide" : "show"}
                </span>
              </a>
            </FlexChildren>

            <AboutContainer showAbout={showAbout}>
              <p>
                This is an tool for exploring how{" "}
                <a href="http://colah.github.io/posts/2014-07-NLP-RNNs-Representations/">
                  Word Embeddings
                </a>{" "}
                relate to each other through a "calculator" inspired interface.
              </p>
              <p>
                It uses the{" "}
                <a href="https://nlp.stanford.edu/projects/glove/">GloVe 6B</a>{" "}
                pretrained vectors, and is intended as an educational tool only.
                It is still a work-in-progress, and was made by <a href="http://a9.io">Max Krieger</a>.
              </p>
              <p>Try these combinations:</p>
              <ul>
                <li>
                  <Word>king</Word>
                  <Operator>-</Operator>
                  <Word>man</Word>
                  <Operator>+</Operator>
                  <Word>woman</Word>
                </li>
                <li>
                  <Word>standing</Word>
                  <Operator>+</Operator>
                  <Word>chair</Word>
                </li>
                <li>
                  <Word>lemon</Word>
                  <Operator>-</Operator>
                  <Word>yellow</Word>
                  <Operator>+</Operator>
                  <Word>green</Word>
                </li>
                <li>
                  <Word>language</Word>
                  <Operator>+</Operator>
                  <Word>tokyo</Word>
                </li>
              </ul>
            </AboutContainer>
          </About>
        </Content>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/App.tsx